import { classifyRoom, assignClustersFromRange, extractRoomNumberIndex } from "./roomNumberingMethods";
function roomCentroid(room) {
    const pts = room.polygon;
    let sx = 0, sy = 0;
    for (const [x, y] of pts) {
        sx += x;
        sy += y;
    }
    return [sx / pts.length, sy / pts.length];
}
function dist2D(a, b) {
    return Math.hypot(a[0] - b[0], a[1] - b[1]);
}
/** Strips any existing building+floor prefix (everything up to and including the last ".") and applies a new one. */
function reprefixNumber(num, building, floorIndex) {
    const dotIdx = num.lastIndexOf(".");
    const suffix = dotIdx >= 0 ? num.slice(dotIdx + 1) : num;
    return `${buildingFloorPrefix(building, floorIndex)}${suffix}`;
}
function buildingFloorPrefix(building, floorIndex) {
    const b = building?.trim() ?? "";
    const f = floorIndex.toString().padStart(2, "0");
    return `${b}${f}.`;
}
function minDistForRoom(roomNumber, grid, distMap) {
    let min = Infinity;
    for (const node of grid.nodes) {
        if (!(node.rooms ?? []).includes(roomNumber))
            continue;
        const d = distMap.get(node.id)?.dist ?? Infinity;
        if (d < min)
            min = d;
    }
    return min;
}
/**
 * Finds the closest room of the same `classifyRoom` type on a floor within maxDistanceM.
 * Returns null if none found.
 */
function findClosestMatchingRoom(coord, sourceType, floor, maxDistanceM) {
    let best = null;
    let bestDist = maxDistanceM;
    for (const room of floor.rooms) {
        if (classifyRoom(room.roomName) !== sourceType)
            continue;
        const d = dist2D(coord, roomCentroid(room));
        if (d <= bestDist) {
            bestDist = d;
            best = room;
        }
    }
    return best;
}
/** Returns the next integer after `from` that doesn't end in 0 or 5 and is ≤ `max`. */
function nextSpecialRoomNumber(from, max) {
    let n = from + 1;
    while (n <= max) {
        if (n % 10 !== 0 && n % 10 !== 5)
            return n;
        n++;
    }
    return null;
}
/**
 * Groups rooms of a given type across all floors into vertical clusters by XY proximity.
 * Clusters are built greedily from the lowest floor upward; the anchor coord is the
 * centroid of the first (lowest-floor) instance.
 */
function buildVerticalClusters(sortedFloors, type, maxDistanceM) {
    const clusters = [];
    for (const floor of sortedFloors) {
        const matching = floor.rooms.filter((r) => classifyRoom(r.roomName) === type);
        for (const room of matching) {
            const coord = roomCentroid(room);
            const existing = clusters.find((c) => dist2D(c.coord, coord) <= maxDistanceM);
            if (existing) {
                existing.rooms.push({ room, floor });
            }
            else {
                clusters.push({ rooms: [{ room, floor }], coord });
            }
        }
    }
    return clusters;
}
function toGenericClusters(clusters) {
    return clusters.map((c) => ({
        rooms: c.rooms.map((e) => ({
            room: {
                roomNumber: e.room.roomNumber,
                manualRoomNumber: e.room.manualRoomNumber,
                approved: e.room.approved,
            },
            floorIndex: e.floor.floorIndex,
        })),
    }));
}
export function assignStaircaseNumbers(currentFloor, allFloors, grid, distMap, maxDistanceM = 2) {
    const sortedFloors = [...allFloors].sort((a, b) => a.floorIndex - b.floorIndex);
    const clusters = buildVerticalClusters(sortedFloors, "staircase", maxDistanceM);
    // Sort clusters by the minimum distance of any staircase room on the current floor
    clusters.sort((a, b) => {
        const distA = Math.min(...a.rooms
            .filter((e) => e.floor.floorIndex === currentFloor.floorIndex)
            .map((e) => minDistForRoom(e.room.roomNumber, grid, distMap)));
        const distB = Math.min(...b.rooms
            .filter((e) => e.floor.floorIndex === currentFloor.floorIndex)
            .map((e) => minDistForRoom(e.room.roomNumber, grid, distMap)));
        return distA - distB;
    });
    const raw = assignClustersFromRange(toGenericClusters(clusters), currentFloor.floorIndex, 861, 880);
    const roomByNumber = new Map(currentFloor.rooms.map((r) => [r.roomNumber, r]));
    const output = {};
    for (const [roomNumber, num] of Object.entries(raw)) {
        const building = roomByNumber.get(roomNumber)?.building;
        output[roomNumber] = reprefixNumber(num, building, currentFloor.floorIndex);
    }
    return output;
}
const ELEVATOR_BASE = 880;
const ELEVATOR_RANGE_END = 899;
function extractElevatorSuffixNumber(roomName) {
    if (!roomName)
        return null;
    const parts = roomName.split(/\s+/);
    for (let i = parts.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(parts[i]))
            return parseInt(parts[i], 10);
    }
    return null;
}
export function assignElevatorNumbers(currentFloor, allFloors, maxDistanceM = 2) {
    const sortedFloors = [...allFloors].sort((a, b) => a.floorIndex - b.floorIndex);
    const clusters = buildVerticalClusters(sortedFloors, "elevator", maxDistanceM);
    const output = {};
    const taken = new Set();
    const roomByNumber = new Map(currentFloor.rooms.map((r) => [r.roomNumber, r]));
    const prefix = (roomNumber) => buildingFloorPrefix(roomByNumber.get(roomNumber)?.building, currentFloor.floorIndex);
    const pending = [];
    // Pass 1: resolve approved and manual entries (lowest floor wins), register numbers as taken
    for (const cluster of clusters) {
        const currentFloorRooms = cluster.rooms.filter((e) => e.floor.floorIndex === currentFloor.floorIndex);
        if (currentFloorRooms.length === 0)
            continue;
        const approvedEntry = cluster.rooms.find((e) => e.room.approved && e.room.manualRoomNumber?.trim());
        if (approvedEntry) {
            const num = approvedEntry.room.manualRoomNumber.trim();
            const n = parseInt(num, 10);
            if (!isNaN(n))
                taken.add(n);
            for (const { room } of currentFloorRooms)
                output[room.roomNumber] = num;
            continue;
        }
        const manualEntry = cluster.rooms.find((e) => e.room.manualRoomNumber?.trim());
        if (manualEntry) {
            const num = manualEntry.room.manualRoomNumber.trim();
            const n = parseInt(num, 10);
            if (!isNaN(n))
                taken.add(n);
            for (const { room } of currentFloorRooms)
                output[room.roomNumber] = num;
            continue;
        }
        pending.push({ cluster, currentFloorRooms });
    }
    // Pass 2: try to assign from room name suffix (e.g. "Heis 3" → 880 + 3 = 883), skip if taken
    const stillPending = [];
    for (const item of pending) {
        const suffixEntry = item.cluster.rooms.find((e) => extractElevatorSuffixNumber(e.room.roomName) != null);
        if (suffixEntry) {
            const derived = ELEVATOR_BASE + extractElevatorSuffixNumber(suffixEntry.room.roomName);
            if (derived > ELEVATOR_BASE && derived <= ELEVATOR_RANGE_END && !taken.has(derived)) {
                taken.add(derived);
                for (const { room } of item.currentFloorRooms)
                    output[room.roomNumber] = `${prefix(room.roomNumber)}${derived}`;
                continue;
            }
        }
        stillPending.push(item);
    }
    // Pass 3: assign remaining in order, skipping taken numbers
    let autoLast = ELEVATOR_BASE;
    for (const { currentFloorRooms } of stillPending) {
        let next = autoLast + 1;
        while (next <= ELEVATOR_RANGE_END && taken.has(next))
            next++;
        if (next > ELEVATOR_RANGE_END)
            break;
        autoLast = next;
        taken.add(next);
        for (const { room } of currentFloorRooms)
            output[room.roomNumber] = `${prefix(room.roomNumber)}${next}`;
    }
    return output;
}
const CORRIDOR_TYPES = new Set(["corridor", "numbered_corridor"]);
/**
 * Assigns room numbers to all corridors and corridor-like rooms (forrom etc.) across all
 * floors (range 801–859, skipping x0/x5). Each corridor room gets its own unique number —
 * corridors are enumerated floor by floor (lowest first), then in declaration order within
 * each floor.
 *
 * Priority per room: manual number → previously calculated number → auto-generated.
 */
export function assignCorridorNumbers(currentFloor, grid, distMap) {
    const output = {};
    const corridors = currentFloor.rooms
        .filter((r) => CORRIDOR_TYPES.has(classifyRoom(r.roomName)))
        .map((r) => ({ room: r, minDist: minDistForRoom(r.roomNumber, grid, distMap) }))
        .sort((a, b) => a.minDist - b.minDist)
        .map(({ room }) => room);
    // Pre-claim manual numbers so auto-generation never lands on one already in use.
    const taken = new Set();
    for (const room of corridors) {
        const n = extractRoomNumberIndex(room.manualRoomNumber?.trim() ?? "");
        if (n != null)
            taken.add(n);
    }
    let autoLast = 800;
    for (const room of corridors) {
        if (room.manualRoomNumber?.trim()) {
            output[room.roomNumber] = room.manualRoomNumber.trim();
            continue;
        }
        let next = nextSpecialRoomNumber(autoLast, 860);
        while (next != null && taken.has(next))
            next = nextSpecialRoomNumber(next, 860);
        if (next != null) {
            autoLast = next;
            taken.add(next);
            output[room.roomNumber] = `${buildingFloorPrefix(room.building, currentFloor.floorIndex)}${next}`;
        }
    }
    return output;
}
/**
 * Assigns shaft (sjakt) numbers in the 900–920 range, shared vertically across
 * a cluster's floors (manual/approved on the lowest floor wins; otherwise
 * auto-generated once per cluster and reused on every floor it appears on).
 */
export function assignShaftNumbers(currentFloor, allFloors, maxDistanceM = 2) {
    const sortedFloors = [...allFloors].sort((a, b) => a.floorIndex - b.floorIndex);
    const clusters = buildVerticalClusters(sortedFloors, "shaft", maxDistanceM);
    const raw = assignClustersFromRange(toGenericClusters(clusters), currentFloor.floorIndex, 900, 920);
    const roomByNumber = new Map(currentFloor.rooms.map((r) => [r.roomNumber, r]));
    const output = {};
    for (const [roomNumber, num] of Object.entries(raw)) {
        const building = roomByNumber.get(roomNumber)?.building;
        output[roomNumber] = reprefixNumber(num, building, currentFloor.floorIndex);
    }
    return output;
}
/**
 * Classifies a technical room into a discipline sub-type used for number range selection.
 * Returns null if the room name doesn't match any known technical discipline.
 */
export function classifyTechnicalSubType(roomName) {
    if (!roomName)
        return null;
    const lower = roomName.toLowerCase();
    if (/vvs|sjakt|varmesentral|varmepumpe|varmeentr/.test(lower))
        return "vvs";
    if (/elkraft|el[- ]?tavle|tavlerom|\buf-el\b/.test(lower))
        return "elkraft";
    if (/\bikt\b|serverrom|datarom/.test(lower))
        return "ikt";
    if (/h[øo]yspent|trafo|transformator/.test(lower))
        return "hoyspent";
    return null;
}
const TECHNICAL_SUB_RANGES = {
    vvs: [901, 939],
    elkraft: [941, 949],
    ikt: [951, 959],
    hoyspent: [991, 999],
};
/**
 * Assigns room numbers to all technical rooms and shafts across all floors,
 * routing each room into its discipline sub-range (skipping x0/x5):
 *   901–939  VVS
 *   941–949  Elkraft
 *   951–959  IKT
 *   991–999  Høyspent / trafo
 *
 * Rooms that don't match a known sub-type are skipped.
 * Enumerated floor by floor (lowest first), then in declaration order within each floor.
 * Priority per room: manual number → previously calculated number → auto-generated.
 */
export function assignTechnicalRoomNumbers(currentFloor) {
    const output = {};
    const autoLast = {
        vvs: 900,
        elkraft: 940,
        ikt: 950,
        hoyspent: 990,
    };
    const technicalRooms = currentFloor.rooms.filter((r) => classifyRoom(r.roomName) === "technical");
    // Pre-claim manual numbers per sub-type so auto-generation never lands on one already in use.
    const taken = { vvs: new Set(), elkraft: new Set(), ikt: new Set(), hoyspent: new Set() };
    for (const room of technicalRooms) {
        const subType = classifyTechnicalSubType(room.roomName);
        if (!subType)
            continue;
        const manual = room.manualRoomNumber?.trim() ?? "";
        const n = extractRoomNumberIndex(manual) ?? (/^\d+$/.test(manual) ? parseInt(manual, 10) : null);
        if (n != null)
            taken[subType].add(n);
    }
    for (const room of technicalRooms) {
        const subType = classifyTechnicalSubType(room.roomName);
        if (!subType)
            continue;
        // Priority 1: manual number
        if (room.manualRoomNumber?.trim()) {
            output[room.roomNumber] = room.manualRoomNumber.trim();
            continue;
        }
        // Auto-generate within the sub-range — enumerate within this floor only
        const [, rangeEnd] = TECHNICAL_SUB_RANGES[subType];
        let next = nextSpecialRoomNumber(autoLast[subType], rangeEnd);
        while (next != null && taken[subType].has(next))
            next = nextSpecialRoomNumber(next, rangeEnd);
        if (next != null) {
            autoLast[subType] = next;
            taken[subType].add(next);
            output[room.roomNumber] = next.toString();
        }
    }
    return output;
}
